import PropTypes from "prop-types";
import classNames from "classnames";

import moment from "moment";
import "moment/locale/tr";

import LocationIcon from "assets/svg/location_icon.svg";
import FinishedIcon from "assets/svg/finished.svg";
import { roomTypeData } from "@/mock/room-type";

import { priceFormatter } from "@/src/helpers/number-formatter";
import { Ga4WithEventParameters } from "@/src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import { LazyImage } from "src/components/common/lazy-image/LazyImage";
import Unit from "@/src/utils/constants/units";

import styles from "./styles.module.css";

const HighlighterListingProjectCard = ({ listItemInfo, isEmlakjetLink, isProjectDetail, isCompanyDetail, index }) => {
  const {
    name: title,
    area,
    flatCount,
    category,
    price: { priceVisibility, priceSummary },
    unitTypes,
    locationInfo: { cityName, districtName },
    companyName,
    deliveryDate,
    isImmediateDelivery,
    images: coverPhoto,
    url: href,
    salesStatus,
    commissionRate,
  } = listItemInfo || {};
  const roomTypes = [...new Set(unitTypes?.map((item) => item.roomType))];
  const unitAreas = unitTypes?.map((item) => item?.unitArea);

  const isFirstDisplayedListingCard = [0, 1, 2, 3].includes(index);

  const titleClass = classNames({
    [styles.title]: true,
    [styles.hiddenOverflow]: true,
  });

  const quickinfoClass = classNames({
    [styles.quickinfo]: true,
  });

  const locationWrapperClass = classNames({
    [styles.locationWrapper]: true,
    [styles.hiddenOverflow]: true,
  });

  const wrapperClass = classNames({
    [styles.wrapper]: true,
    [styles.wrapperForEmlakjetLink]: isEmlakjetLink,
    [styles.wrapperForCompanyDetail]: isCompanyDetail,
    [styles.wrapperForProjectDetail]: isProjectDetail,
  });

  const contentWrapperClass = classNames({
    [styles.contentWrapper]: true,
    [styles.contentWrapperForEmlakjetLink]: isEmlakjetLink,
    [styles.contentWrapperForCompanyDetail]: isCompanyDetail,
  });

  const priceClass = classNames({
    [styles.price]: true,
    [styles.companyDetailPrice]: isCompanyDetail,
  });

  const projectFinishedClasses = classNames({
    [styles.projectFinished]: true,
    [styles.projectFinishedForCompanyDetail]: isCompanyDetail,
  });

  const momentDate = moment(deliveryDate);
  momentDate.locale("tr");

  const projectDeliveryTime = isImmediateDelivery
  ? "Hemen Teslim"
  : deliveryDate 
    ? `${momentDate.format("MMMM YYYY")} Teslim`
    : "";

  const handleClick = () => {
    localStorage.setItem("last_view", "project");
    Ga4WithEventParameters({
      eventName: "emlakjet-link-proje-kartlari",
      eventParameters: { label: title },
    });
  };

  const getRoomTypeNames = (ids) => {
    return ids
      .map((id) => {
        const match = roomTypeData.find((item) => item[id]);
        return match[id] || "";
      })
      .filter(Boolean);
  };
  const displayedRoomTypes = getRoomTypeNames(roomTypes.slice(0, 2)).join(",");
  const hasStudioType = getRoomTypeNames(roomTypes.slice(0, 2)).includes('Stüdyo');
  const tooltipContent = getRoomTypeNames(roomTypes).join(" | ");
  const extraRoomCount = roomTypes.length - 2;
  const projectCardCoverPhoto = coverPhoto[0] || "/assets/images/no_photo_big.jpg";

  return (
    <div className="w-full">
      <a className={wrapperClass} href={href} onClick={handleClick}>
        <div className={styles.imageWrapper}>
          <LazyImage
            src={projectCardCoverPhoto}
            alt="showcase_project_image"
            className={styles.imageClass}
            visibleByDefault={isFirstDisplayedListingCard}
          />
          {commissionRate && isEmlakjetLink && <div className={styles.commissionRate}>%{commissionRate} KOMİSYON</div>}
        </div>
        <div className={contentWrapperClass}>
          <div className={titleClass}>{title}</div>
          {!isCompanyDetail && (
            <div className={locationWrapperClass}>{!isEmlakjetLink && <span>{companyName}</span>}</div>
          )}
          <div className={styles.isCompanyDetailclasses}>
            <div className={quickinfoClass}>
              {unitAreas?.length > 1 && (
                <>
                  <span>
                    {priceFormatter(Math.min(...unitAreas))} - {priceFormatter(Math.max(...unitAreas))} {Unit.M2.name}
                  </span>
                  {(roomTypes.length > 0 || flatCount) && <span className={styles.divider}>|</span>}
                </>
              )}
            </div>
            {roomTypes.length > 0 && category.slug !== "isyeri" && (
              <div className={styles.roomTypes}>
                {hasStudioType ? 'Stüdyo' : displayedRoomTypes}
                {(extraRoomCount > 0 || hasStudioType) && (
                  // eslint-disable-next-line react/no-unknown-property
                  <div tooltip={tooltipContent} className={styles.roomTypesToolTip}>
                    <span className={styles.hiddenOverflow}>, +{hasStudioType ? extraRoomCount + 1 : extraRoomCount} Oda Tipi</span>
                  </div>
                )}
              </div>
            )}
            {category?.slug === "isyeri" && flatCount ? (
              <span className={styles.landAndWorkPlaceText}> {flatCount} Ünite</span>
            ) : null}
            {category?.slug === "arsa" && area && (
              <span className={styles.landAndWorkPlaceText}>
                {area} {Unit.M2.name}
              </span>
            )}
          </div>
          {projectDeliveryTime && !isEmlakjetLink ? (
            <div className={`${styles.hiddenOverflow} ${styles.projectDeliveryTime}`}>{projectDeliveryTime}</div>
          ) : <div className={styles.spacer}/>}
          {isEmlakjetLink && (
            <div className={locationWrapperClass}>
              <LocationIcon />
              <span className={styles.location}>
                {cityName} - {districtName}
              </span>
            </div>
          )}
          {salesStatus === "FINISHED" ? (
            <div className={projectFinishedClasses}>
              <FinishedIcon />
              <span className={styles.finishedTitle}>Satışı Tamamlandı</span>
            </div>
          ) : (
            <div className={`${styles.hiddenOverflow} ${styles.priceWrapper}`}>
              {(priceVisibility === "ASK" || priceVisibility === "HIDDEN") && (
                <span className={styles.learnPrice}>Fiyat Sor</span>
              )}
              {priceVisibility === "STARTCOUNT" && (
                <span>
                  {priceSummary && <span className={priceClass}> {priceSummary}</span>}
                  <span className={styles.startCount}>{`'den başlayan`}</span>
                </span>
              )}
              {priceVisibility === "RANGE" && <span className={priceClass}>{priceSummary}</span>}
            </div>
          )}
        </div>
      </a>
    </div>
  );
};

HighlighterListingProjectCard.propTypes = {
  onClick: PropTypes.func,
  listItemInfo: PropTypes.object,
  isEmlakjetLink: PropTypes.bool,
  isCompanyDetail: PropTypes.bool,
  isProjectDetail: PropTypes.bool,
  index: PropTypes.number,
};

export default HighlighterListingProjectCard;
